import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { logout as logoutAction } from "../features/auth/authSlice";
import { logoutAdminSession } from "../services/adminSession";
import {
  ADMIN_SESSION_TIMEOUT_MS,
  ADMIN_SESSION_ACTIVITY_KEY,
  ADMIN_SESSION_LOGOUT_KEY,
  ADMIN_SESSION_EXPIRED_MESSAGE,
  ADMIN_PUBLIC_PATHS,
  isProtectedAdminPath,
  createAdminSessionChannel,
  publishAdminActivity,
  clearAdminSessionActivity,
  broadcastAdminLogout,
  getRemainingSessionMs,
  persistAdminSessionMessage
} from "../utils/adminSessionTimeout";

const ACTIVITY_EVENTS = ["click", "touchstart", "keydown", "scroll"];

export default function AdminSessionTimeoutManager() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const timerRef = useRef(null);
  const logoutRef = useRef(false);
  const channelRef = useRef(null);
  const lastPublishedActivityRef = useRef(0);

  const isSessionProtectedRoute =
    isProtectedAdminPath(location.pathname) || !ADMIN_PUBLIC_PATHS.has(location.pathname);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearClientSession = useCallback(() => {
    dispatch(logoutAction());
  }, [dispatch]);

  const performLogout = useCallback(
    async ({
      broadcast = false,
      message = ADMIN_SESSION_EXPIRED_MESSAGE,
      reason = "inactive"
    } = {}) => {
      if (logoutRef.current) {
        return;
      }

      logoutRef.current = true;
      clearTimer();

      if (broadcast) {
        broadcastAdminLogout({ reason, message });
        channelRef.current?.postMessage({
          type: "logout",
          reason,
          message,
          timestamp: Date.now()
        });
      } else if (message) {
        persistAdminSessionMessage(message);
      }

      clearAdminSessionActivity();
      clearClientSession();

      try {
        await logoutAdminSession();
      } catch (error) {
        console.log(error);
      }

      navigate("/", { replace: true });
    },
    [clearClientSession, clearTimer, navigate]
  );

  const scheduleTimeout = useCallback(
    (lastActivityAt) => {
      clearTimer();

      const remainingMs = getRemainingSessionMs(lastActivityAt, ADMIN_SESSION_TIMEOUT_MS);

      if (remainingMs <= 0) {
        performLogout({
          broadcast: true,
          message: ADMIN_SESSION_EXPIRED_MESSAGE,
          reason: "inactive"
        });
        return;
      }

      timerRef.current = window.setTimeout(() => {
        performLogout({
          broadcast: true,
          message: ADMIN_SESSION_EXPIRED_MESSAGE,
          reason: "inactive"
        });
      }, remainingMs);
    },
    [clearTimer, performLogout]
  );

  const markActivity = useCallback(
    (force = false) => {
      if (!isSessionProtectedRoute || logoutRef.current) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastPublishedActivityRef.current < 1000) {
        return;
      }

      lastPublishedActivityRef.current = now;
      publishAdminActivity(now);
      channelRef.current?.postMessage({
        type: "activity",
        timestamp: now
      });
      scheduleTimeout(now);
    },
    [isSessionProtectedRoute, scheduleTimeout]
  );

  useEffect(() => {
    if (!isSessionProtectedRoute) {
      logoutRef.current = false;
      clearTimer();
      return undefined;
    }

    logoutRef.current = false;
    const storedActivity = Number(window.localStorage.getItem(ADMIN_SESSION_ACTIVITY_KEY) || 0);
    const initialActivity = storedActivity > 0 ? storedActivity : Date.now();
    if (!storedActivity) {
      publishAdminActivity(initialActivity);
    }
    lastPublishedActivityRef.current = initialActivity;
    scheduleTimeout(initialActivity);

    const handleStorage = (event) => {
      if (event.key === ADMIN_SESSION_ACTIVITY_KEY && event.newValue) {
        const nextActivity = Number(event.newValue || 0);
        if (Number.isFinite(nextActivity) && nextActivity > 0) {
          lastPublishedActivityRef.current = nextActivity;
          scheduleTimeout(nextActivity);
        }
      }

      if (event.key === ADMIN_SESSION_LOGOUT_KEY && event.newValue) {
        const payload = JSON.parse(event.newValue || "{}");
        performLogout({
          broadcast: false,
          message: payload?.message || ADMIN_SESSION_EXPIRED_MESSAGE,
          reason: payload?.reason || "sync"
        });
      }
    };

    const handleBroadcast = (event) => {
      if (event?.data?.type === "activity" && event.data.timestamp) {
        lastPublishedActivityRef.current = event.data.timestamp;
        scheduleTimeout(event.data.timestamp);
      }

      if (event?.data?.type === "logout") {
        performLogout({
          broadcast: false,
          message: event.data.message || ADMIN_SESSION_EXPIRED_MESSAGE,
          reason: event.data.reason || "sync"
        });
      }
    };

    channelRef.current = createAdminSessionChannel();
    channelRef.current?.addEventListener("message", handleBroadcast);

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    window.addEventListener("storage", handleStorage);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity, { passive: true });
      });
      window.removeEventListener("storage", handleStorage);
      channelRef.current?.removeEventListener("message", handleBroadcast);
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [clearTimer, isSessionProtectedRoute, markActivity, performLogout, scheduleTimeout]);

  useEffect(() => {
    if (!isSessionProtectedRoute) {
      return;
    }

    markActivity(true);
  }, [isSessionProtectedRoute, location.pathname, markActivity]);

  return null;
}
